// ============================================================================
// ui/showcase-admin.js — publish a design to "Show me what's possible"
// ============================================================================
// Ryan 2026-07-29: *"a feature for admins which allows them to make designs in the portal WD which
// they can then save so they get added to the 'Show me what's possible' section… when we get
// customers to give us permission to use their designs for advertising, we can recreate them or load
// them into this new feature, add them to the list, and then they go on display automatically…
// admins should be able to pick different layouts for all different weights too."*
//
// Two halves in one dialog:
//   PUBLISH — the design on screen, a name, a caption, and a VARIANT TABLE: one row per weight, each
//     with its own layout. Each row becomes its own slide in the parade (Ryan's choice when asked),
//     so one save can teach 45-in-Varsity-Arch, 25-in-Fieldhouse and 10-logo-only.
//   MANAGE — what is live now: show/hide, reorder, delete, and "Open" (which loads the example back
//     into the designer through `ctx.applyEnvelope`, the same path My Designs uses). That last one is
//     how a customer's permitted design gets recreated: open it, change it, publish again.
//
// ADMIN-ONLY, and the server agrees: every write here goes to a route that checks `rep.admin`
// itself. This module only decides whether to draw the entry point.
// ============================================================================

import { h } from './dom.js';
import { openModal } from './modal.js';
import { isRepMode } from '../rep-mode.js';
import { layoutPresetsForProduct } from '../layouts.js';
import { identityKey } from '../design-profile.js';

import { renderDesignToSVGString } from '../render-engine.js';

import { portalApi as api, portalSession } from '../portal-api.js';

/* The three canvases, in the order Ryan authors them. Mirrors the worker's own FAMILIES list
   (handlers/weight-designer-showcase.js) — the app owns the product-to-family MAPPING
   (`layoutFamilyFor`), the worker owns the validation, and this is only the display order. */
const FAMILY_ORDER = ['plate', 'grip', 'dumbbell'];

/** Only an admin sees the entry point. The routes enforce it too — this is the UI half. */
export function isShowcaseAdmin() {
  const s = portalSession();
  return !!(isRepMode() && s && s.admin);
}

const ICON_TRASH =
  '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">' +
  '<path d="M3 6h18M8 6V4h8v2"/><path d="M5 6l1 14h12l1-14"/></svg>';

/** The showcase icon — a spotlight/star, distinct from Save's floppy and PDF's page. */
export const ICON_SHOWCASE =
  '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">' +
  '<path d="M12 3l2.2 4.8 5.3.6-3.9 3.6 1 5.2L12 14.9 7.4 17.2l1-5.2L4.5 8.4l5.3-.6z"/></svg>';

/**
 * Open the publish/manage dialog.
 * @param {object} ctx the app context — needs `shareEnvelope`, `applyEnvelope`, `state`, `el`, `status`
 */
export async function openShowcaseDialog(ctx) {
  /* Only `manageSlides` still needs this, to name a stored slide's layout when the entry itself
     does not carry a product. Everything the PUBLISH half used to read off the live stage now comes
     from the capture records — see `gatherFamilies`. */
  const product = ctx.state?.ui?.product || 'bumper|Competition Urethane';

  const status = h('p', { class: 'wd-sc-status', role: 'status' });
  const nameInput = h('input', { type: 'text', class: 'wd-dm-name', placeholder: 'e.g., Denver Broncos', maxlength: '80', autocomplete: 'off' });
  const capInput = h('input', { type: 'text', class: 'wd-dm-name', placeholder: 'One line under the plate (optional)', maxlength: '140', autocomplete: 'off' });
  /* WHAT IS IN THE SET, as a readout rather than a form. Ryan: *"I just need to be able to go into
     that menu and click save once, and know it'll save the art for all the weights."* So the dialog
     states what it is about to publish and asks for nothing — the only honest shape once every field
     it used to ask for is already answered by the designs themselves. */
  const setLine = h('p', { class: 'wd-sc-setline' });
  const listEl = h('div', { class: 'wd-sc-list' });

  const say = (m) => { status.textContent = m || ''; };

  const FAMILY_LABEL = { plate: 'Bumper', grip: 'Grip plate', dumbbell: 'Dumbbell / fixed barbell' };
  /** Name the set the button will publish, so "click save once" is a promise you can check first.
   *
   *  ⚠ IT MUST BE ABLE TO SAY "ONE", and 267 shipped believing it had. This line and `publish` read
   *  the same `gatherFamilies`, so whatever it names is exactly what goes — including a set of one.
   *  The amber state is the tell that it is not the three Ryan is authoring. */
  function paintSetLine() {
    const { designs, meta } = gatherFamilies();
    const have = FAMILY_ORDER.filter((f) => designs[f]);
    const missing = FAMILY_ORDER.filter((f) => !designs[f]);
    const stale = have.filter((f) => meta[f] && meta[f].stale);
    let text;
    if (!have.length) {
      text = 'Nothing designed yet — design a bumper, a grip plate and a dumbbell, then publish.';
    } else {
      text = `This set: ${have.map((f) => FAMILY_LABEL[f]).join(' · ')}`;
      text += missing.length
        ? ` — nothing designed for ${missing.map((f) => FAMILY_LABEL[f].toLowerCase()).join(' or ')} yet.`
        : ' — all three models.';
      // Named rather than silently refreshed here: opening a dialog is not the moment to move the
      // stage under someone. Publish does the refresh, and says so while it happens.
      if (stale.length) {
        text += ` The ${stale.map((f) => FAMILY_LABEL[f].toLowerCase()).join(' and ')} `
          + `${stale.length === 1 ? 'was' : 'were'} designed before the current logo — publishing will refresh `
          + `${stale.length === 1 ? 'it' : 'them'} first.`;
      }
    }
    setLine.textContent = text;
    setLine.classList.toggle('is-partial', !have.length || missing.length > 0 || stale.length > 0);
  }

  /* ── NO VARIANT TABLE AT ALL (Ryan 2026-08-10) ───────────────────────────────────────────────
   * *"Why does the show me what's possible menu have the drop-down to select the different weights
   * and layouts? Is that outdated/stale?"* … *"the weight number for the presentation will be the
   * defaults for every weight — KISS — Keep It Stupid Simple."*
   *
   * 🔑 BOTH HALVES BELONGED TO THE ONE-DESIGN-PER-ENTRY MODEL. When an entry was a single artwork,
   * the table was how it taught several arrangements — 45 in Varsity Arch, 25 in Fieldhouse. An
   * entry now carries one design PER FAMILY, and each of those designs already knows its own layout
   * AND the denomination it was built at. The table was asking an admin to restate, in a dialog,
   * two things they had just finished deciding in the designer.
   *
   * ⛔ AND THE LAYOUT HALF HAD ALREADY GONE DEAD IN 266 WITHOUT ANYONE NOTICING — `publish` reads
   * the layout off the tray item and discarded whatever the dropdown said. A control that responds
   * and is ignored is worse than no control. On the one path where it still applied it was a
   * footgun: its options come from the product ON SCREEN, so a set published from a bumper could
   * stamp a bumper preset onto the grip slide, and `getLayoutPreset` resolves an out-of-family id
   * to `LAYOUT_PRESETS[0]` — a bumper. The exact fallback 266 exists to fix.
   *
   * One design per family, one slide per family, at the weight it was designed at. Nothing to fill in.
   */

  // ── manage ──────────────────────────────────────────────────────────────────────────────────
  let entries = [];

  async function refresh() {
    const r = await api('/wd-showcase-admin');
    if (!r.ok) { say(r.error); return; }
    entries = Array.isArray(r.entries) ? r.entries : [];
    paint();
  }

  async function move(id, delta) {
    const i = entries.findIndex((e) => e.id === id);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= entries.length) return;
    const next = entries.slice();
    [next[i], next[j]] = [next[j], next[i]];
    entries = next;
    paint();
    const r = await api('/wd-showcase-order', { method: 'POST', body: { order: entries.map((e) => e.id) } });
    if (!r.ok) { say(r.error); await refresh(); }
  }

  async function toggle(id, on) {
    const r = await api('/wd-showcase-order', { method: 'POST', body: { order: entries.map((e) => e.id), enabled: { [id]: on } } });
    if (!r.ok) { say(r.error); return; }
    await refresh();
    say(on ? 'Showing on the storefront.' : 'Hidden from the storefront.');
  }

  async function remove(entry) {
    const go = await openModal({
      mount: ctx.el,
      title: `Remove “${entry.name}” from the showcase?`,
      body: 'Buyers stop seeing it right away. It goes to the trash, so an admin can restore it for a while.',
      actions: [{ id: 'go', label: 'Remove', variant: 'primary' }, { id: 'no', label: 'Cancel', variant: 'ghost' }],
    });
    if (go !== 'go') return;
    const r = await api('/wd-showcase-delete', { method: 'POST', body: { id: entry.id } });
    if (!r.ok) { say(r.error); return; }
    say(`Removed ${entry.name}.`);
    await refresh();
  }

  // ── REMOVE ONE SLIDE, NOT THE WHOLE EXAMPLE (Ryan 2026-08-03) ────────────────────────────────
  // *"I accidentally added one weight plate twice, once as 45 and once as 25… they're grouped as
  // '2 slides'… now there's no way to delete just 1 of them."* The manage list has always been
  // per-ENTRY (show/hide, reorder, delete), so a mis-keyed variant could only be fixed by deleting
  // the example and rebuilding it — losing the logo upload and its position in the running order.
  //
  // Entirely client-side: `/wd-showcase-save` already updates in place when given an `id`, and its
  // handler states that **an update may omit the design** (it keeps the stored envelope), so
  // dropping a slide costs one small POST instead of re-uploading a multi-megabyte logo.
  //   ⚠ The variant DETAIL is not in the admin list — `summary()` reduces variants to a COUNT — so
  // this reads the full entry first. That is also why the removal posts `got.entry`'s own name and
  // caption back: a partial update would blank whatever it omitted.
  //   ⚠ `cleanVariants` falls back to the STORED list when the incoming one is empty, so removing
  // the final slide would silently restore it. The last row's control is disabled and points at
  // "Remove" instead — deleting the example is what that action actually means.
  async function manageSlides(entry) {
    const got = await api(`/wd-showcase/${encodeURIComponent(entry.id)}`);
    if (!got.ok || !got.entry) { say(got.error || 'Couldn’t open it.'); return; }
    let variants = Array.isArray(got.entry.variants) ? got.entry.variants.slice() : [];
    const slidesEl = h('div', { class: 'wd-sc-list' });
    const note = h('p', { class: 'wd-sc-status', role: 'status' });
    const presetNameFor = (v) => {
      const id = v && v.layoutPreset;
      if (!id) return 'stored layout';
      const found = (layoutPresetsForProduct(v.product || product) || []).find((pp) => pp.id === id);
      return found ? found.name : id;
    };
    async function dropSlide(i) {
      const next = variants.slice();
      next.splice(i, 1);
      note.textContent = 'Removing…';
      const r = await api('/wd-showcase-save', {
        method: 'POST',
        // No `design` — the worker keeps the stored envelope for an update.
        body: {
          id: entry.id,
          name: got.entry.name,
          caption: got.entry.caption || '',
          variants: next,
          enabled: got.entry.enabled !== false,
        },
      });
      if (!r.ok) { note.textContent = r.error || 'Couldn’t remove that slide.'; return; }
      variants = next;
      paintSlides();
      note.textContent = `Removed — ${r.slides} slide${r.slides === 1 ? '' : 's'} left.`;
      await refresh();
      if (!gridEl.hidden) await paintPreview();
    }
    function paintSlides() {
      const last = variants.length <= 1;
      slidesEl.replaceChildren(...variants.map((v, i) => h('div', { class: 'wd-sc-item' },
        h('span', { class: 'wd-sc-item__text' },
          h('span', { class: 'wd-dm-row__name' }, `${v.weight} ${v.unit === 'kg' ? 'kg' : 'lb'}`),
          h('span', { class: 'wd-dm-row__meta' }, presetNameFor(v))),
        h('button', {
          type: 'button', class: 'wd-sc-mini wd-sc-mini--danger', html: ICON_TRASH,
          disabled: last ? '' : null,
          title: last ? 'The last slide can’t be removed — use Remove to delete the example' : 'Remove this slide',
          'aria-label': `Remove the ${v.weight}${v.unit === 'kg' ? 'kg' : 'lb'} slide`,
          onClick: () => void dropSlide(i),
        }))));
    }
    paintSlides();
    await openModal({
      mount: ctx.el,
      title: `Slides in “${got.entry.name}”`,
      body: h('div', { class: 'wd-sc' }, slidesEl, note),
      actions: [{ id: 'done', label: 'Done', variant: 'ghost' }],
    });
  }

  async function open(entry) {
    const got = await api(`/wd-showcase/${encodeURIComponent(entry.id)}`);
    if (!got.ok || !got.entry) { say(got.error || 'Couldn’t open it.'); return; }
    const applied = await ctx.applyEnvelope?.(got.entry.design);
    say(applied ? `Opened ${entry.name} — edit it, then publish again.` : 'That example couldn’t be opened.');
  }

  function paint() {
    listEl.innerHTML = '';
    if (!entries.length) {
      listEl.appendChild(h('p', { class: 'wd-dm-empty' }, 'Nothing published yet.'));
      return;
    }
    entries.forEach((e, i) => {
      const on = e.enabled !== false;
      listEl.appendChild(h('div', { class: `wd-sc-item${on ? '' : ' is-off'}` },
        h('span', { class: 'wd-sc-item__text' },
          h('span', { class: 'wd-dm-row__name' }, e.name),
          h('span', { class: 'wd-dm-row__meta' }, `${e.variants || 1} slide${(e.variants || 1) === 1 ? '' : 's'}${on ? '' : ' · hidden'}`)),
        h('button', { type: 'button', class: 'wd-sc-mini', title: 'Move up', disabled: i === 0 ? '' : null, onClick: () => void move(e.id, -1) }, '↑'),
        h('button', { type: 'button', class: 'wd-sc-mini', title: 'Move down', disabled: i === entries.length - 1 ? '' : null, onClick: () => void move(e.id, 1) }, '↓'),
        h('button', { type: 'button', class: 'wd-sc-mini', title: on ? 'Hide from buyers' : 'Show to buyers', onClick: () => void toggle(e.id, !on) }, on ? 'Hide' : 'Show'),
        // Only worth offering when there is more than one — a single-slide entry has nothing to
        // pick from, and its "delete this slide" IS Remove.
        (e.variants || 1) > 1
          ? h('button', { type: 'button', class: 'wd-sc-mini', title: 'Remove individual weights from this example', onClick: () => void manageSlides(e) }, 'Slides')
          : null,
        h('button', { type: 'button', class: 'wd-sc-mini', title: 'Open in the designer', onClick: () => void open(e) }, 'Open'),
        h('button', { type: 'button', class: 'wd-sc-mini wd-sc-mini--danger', title: 'Remove', html: ICON_TRASH, onClick: () => void remove(e) }),
      ));
    });
  }

  /* ── ONE DESIGN PER FAMILY, GATHERED FROM THE PRODUCTS ACTUALLY DESIGNED (Ryan 2026-08-10) ────
   * *"I'm going to add 1 comp bumper, 1 grip plate, and 1 dumbbell to each test… once that's done
   * I'm going to go to save and then go to 'Show me what's possible', and I want to be able to save
   * the set. This will then translate across every single [model] since all bumpers use the same
   * canvas, and all dumbbells/fixed barbells use the same canvas type/style too."*
   *
   * ⛔ THIS READ THE STAGING TRAY UNTIL 268, AND THAT WAS THE BUG. Memory 187 says *"a saved design
   * is a SET, and the set is the staging tray"*, which is true of the tray and false of this flow:
   * tray items are created by an explicit PRIME action, never by designing and saving. Ryan
   * designed three models, hit publish, and the tray was empty — so this fell through to its
   * backstop, the design ON SCREEN, and the set shipped as one slide of `dumbbell` because that is
   * what he happened to finish on.
   *
   * 🔑 THE REAL ANSWER IS THAT THE APP HOLDS ONE DESIGN AND MUTATES IT PER PRODUCT, so the three
   * designs do not exist anywhere until something captures them. `main.js`'s `captureLeavingDesign`
   * is that capture, stamped at `adoptProduct`'s leave seam — the one door every product move goes
   * through. This reads what it captured, folds it by FAMILY, and lets the LIVE stage answer for
   * the model still on screen (which has not been left, so no snapshot of it could be fresher).
   *
   * ⚠ LAST ONE DESIGNED WINS WITHIN A FAMILY, by `seq` rather than by key order: two competition
   * bumper configurations are two products in one family and only one can be that family's slide,
   * and re-visiting a product overwrites its record in place without re-ordering it.
   * ⚠ STALENESS IS REPORTED, NOT SWALLOWED. A snapshot is only wrong in one way — change the logo
   * after leaving the bumper and the stored bumper still wears the old mark — so each capture
   * carries the `identityKey` it was taken under and anything that no longer matches the live
   * design is flagged. `publish` refreshes those by re-visiting the model; see it for why that is
   * a hop rather than a merge.
   */
  function gatherFamilies() {
    const out = {};
    const meta = {};
    const liveId = identityKey(ctx.state?.designProfile || {});
    const take = (cap, stale) => {
      out[cap.family] = cap.env;
      meta[cap.family] = {
        product: cap.product, layoutPreset: cap.layoutPreset, weight: cap.weight, stale,
      };
    };
    // Oldest capture first, so a later one for the same family simply overwrites it.
    for (const cap of (ctx.capturedDesigns?.() || [])) {
      if (cap && cap.env && cap.family) take(cap, cap.idKey !== liveId);
    }
    /* THE LIVE STAGE ALWAYS WINS ITS OWN FAMILY, and it is never stale by construction — it IS the
       live design. `liveDesign()` applies the same three gates as the leave-seam capture (populated,
       family agrees with the product, envelope serialises), so an untouched stock plate cannot
       enter a set here any more than it can there. */
    const live = ctx.liveDesign?.();
    if (live) take(live, false);
    return { designs: out, meta };
  }

  // ── publish ─────────────────────────────────────────────────────────────────────────────────
  /* ── A STALE CAPTURE IS REFRESHED BY RE-VISITING THE MODEL, NEVER BY MERGING (2026-08-10) ──────
   * The one way a captured design can be wrong: change the logo (or the Organization / Mascot
   * wording, or the typeface) after leaving the bumper, and the stored bumper still wears the old
   * mark — so a "set" would ship two different identities across three models, which is worse than
   * shipping two of three.
   *
   * ⛔ AND THE REMEDY IS A HOP, not patching the new identity into the old envelope. Applying an
   * identity to a family is `setLayoutPreset` re-slotting the elements plus the plate colour, ink
   * and nudge restores in `adoptProduct` — an ordering-sensitive sequence that has already absorbed
   * several silent bugs. Driving `switchProduct` re-runs that sequence for real and re-captures
   * through the same seam, so there is exactly one answer to "what is the bumper's design" instead
   * of two that can disagree (project CLAUDE.md §0b).
   *
   * ⚠ IT RETURNS THE ADMIN TO THE MODEL THEY WERE ON. The dialog stays open over the whole thing;
   * the stage moving behind it is the honest picture of what is happening, and `say` narrates it.
   */
  async function refreshStale(stale) {
    const home = ctx.state?.ui?.product || '';
    for (const fam of stale) {
      const key = (fam.meta && fam.meta.product) || '';
      if (!key) continue;
      say(`Refreshing the ${FAMILY_LABEL[fam.family].toLowerCase()} for the current logo…`);
      // eslint-disable-next-line no-await-in-loop
      try { await ctx.switchProduct?.(key); } catch { /* re-gathered below; still-stale is reported */ }
    }
    if (home && home !== ctx.state?.ui?.product) {
      try { await ctx.switchProduct?.(home); } catch { /* the admin can switch back themselves */ }
    }
  }

  async function publish() {
    const name = nameInput.value.trim();
    if (!name) { say('Give this example a name.'); nameInput.focus(); return; }
    let { designs, meta } = gatherFamilies();
    if (!Object.keys(designs).length) { say('There’s no design to publish yet.'); return; }

    /* Refresh anything captured under an older identity, then ask again — `switchProduct` re-runs
       the capture, so the second gather is what actually ships. */
    const stale = FAMILY_ORDER.filter((f) => designs[f] && meta[f] && meta[f].stale)
      .map((f) => ({ family: f, meta: meta[f] }));
    if (stale.length) {
      await refreshStale(stale);
      ({ designs, meta } = gatherFamilies());
      paintSetLine();
      /* ⛔ STILL STALE MEANS REFUSE, NOT SHIP ANYWAY. `switchProduct` early-returns while another
         switch is in flight, so a hop can silently decline — and the whole point of the check is
         that a mixed-identity set must not reach the storefront. Naming the model is what makes
         this a two-second fix rather than a mystery. */
      const left = FAMILY_ORDER.filter((f) => designs[f] && meta[f] && meta[f].stale);
      if (left.length) {
        say(`Couldn’t refresh the ${left.map((f) => FAMILY_LABEL[f].toLowerCase()).join(' or ')} — `
          + 'switch to that model once, then publish again.');
        return;
      }
    }

    const fams = FAMILY_ORDER.filter((f) => designs[f]);

    /* ONE SLIDE PER FAMILY. Everything each slide needs was decided in the designer: the layout the
       admin picked and the denomination they were standing on, both carried on the capture record.
       ⚠ NO `currentWeight` FALLBACK ANY MORE. It used to backstop a family with no recorded weight
       by borrowing the live plate's — which quietly filed a grip slide under a bumper's 45. Every
       capture now carries its own weight by construction (`captureOf` reads the live rung), so a
       zero here means the record is malformed and the family is dropped rather than mislabelled. */
    const variants = fams
      .filter((fam) => Number(meta[fam] && meta[fam].weight) > 0)
      .map((fam) => ({
        weight: Number(meta[fam].weight),
        unit: 'lb',
        family: fam,
        product: meta[fam].product || '',
        layoutPreset: meta[fam].layoutPreset || '',
      }));
    if (!variants.length) { say('There’s no design to publish yet.'); return; }

    /* Send only the envelopes a slide actually draws from. An extra one would be stored, counted in
       the entry's `families`, and then advertised by the feed as drawable on a model that has no
       slide for it — a family list that promises more than the variants can deliver. */
    const payload = {};
    for (const v of variants) payload[v.family] = designs[v.family];

    /* ⚠ SAY WHAT IS MISSING BEFORE SENDING IT. The route refuses an incomplete set and its message
       names the family, but an admin publishing one identity at a time deserves to be told at the
       button rather than by an error — and the whole point of Ryan's pass is that a set covers all
       three. This is a WARNING, not a block: two families is a legitimate thing to publish while
       the third is still being designed. */
    const missing = FAMILY_ORDER.filter((f) => !payload[f]);
    say(missing.length
      ? `Publishing ${variants.length} slide${variants.length === 1 ? '' : 's'} — no `
        + `${missing.map((f) => FAMILY_LABEL[f].toLowerCase()).join(' or ')} design in this set…`
      : `Publishing ${variants.length} slide${variants.length === 1 ? '' : 's'} across all three models…`);
    const r = await api('/wd-showcase-save', {
      method: 'POST',
      /* ⛔ NO LEGACY `design` FIELD — IT WAS THE PLATE ENVELOPE, SENT TWICE (Ryan 2026-08-12:
         *"Request body too large"* publishing a 1 MB chain-link mark on a bumper).
         `designs.plate` already carries that envelope, and the route derives the legacy field from
         it: `designs.plate || body.design || existing.design` (weight-designer-showcase.js ~227).
         So the second copy bought nothing and cost a full envelope — measured at ~1.27 MB on Angry
         Panda, which is most of the headroom under the 6 MB cap this was tripping.
         ⚠ THE LEGACY FIELD IS STILL STORED, just derived rather than sent. It exists for a cached
         feed reader that predates `designs`; a plate set still populates it exactly as before.
         ⛔ And a set with NO plate no longer mirrors its FIRST family into it — that used to hand a
         grip envelope to a reader that draws it on a bumper, which is worse than the empty it now
         gets. */
      body: { name, caption: capInput.value.trim(), designs: payload, variants, enabled: true },
    });
    if (!r.ok) { say(r.error); return; }
    say(`Published — ${name} is on the storefront (${r.slides} slide${r.slides === 1 ? '' : 's'}).`);
    ctx.status?.success?.(`${name} added to “Show me what’s possible”.`);
    nameInput.value = ''; capInput.value = '';
    paintSetLine();   // a model may have been designed while the dialog was open
    await refresh();
    // The sheet is open BECAUSE they are checking their work — leaving it stale would show the
    // storefront without the thing they just published.
    if (!gridEl.hidden) await paintPreview();
  }

  // ── PREVIEW: A CONTACT SHEET, IN THE DIALOG (Ryan 2026-08-03) ─────────────────────────────────
  // *"preview them in the pop-up for easy/quick ability to scan and see what colors/configurations
  // we've already used… like 2D SVGs or something."*
  //
  // ⚠ THE FIRST ATTEMPT OPENED THE 3D BUYER PARADE AND TRAPPED HIM IN IT. Two lessons, both worth
  // more than the feature:
  //   1. **A screen with no exit of its own is only safe while its ONE exit is guaranteed.** The
  //      parade has no close button by design (showcase.js:81 — "dismiss via 'Design your own'"),
  //      and that chip is hidden forever once `retire()` runs, which is exactly what uploading a
  //      logo does. Entering from here meant a room whose only door had been bricked up. `enter()`
  //      now refuses when the chip is retired, so no future caller can rebuild the trap.
  //   2. **"Preview" meant SCAN, not WATCH.** The parade shows one plate at a time with a dramatic
  //      3D spin — the opposite of "what colours have we already used?". The right shape is every
  //      published slide at once, small, flat, and in the dialog he is already looking at.
  //
  // Renders the same `renderDesignToSVGString` the plate itself uses, so a tile cannot drift from
  // what ships. Re-fetches on each open rather than caching: the admin's whole reason to look is
  // that they just changed something.
  const gridEl = h('div', { class: 'wd-sc-grid', hidden: true });

  async function paintPreview() {
    gridEl.replaceChildren(h('p', { class: 'wd-dm-empty' }, 'Rendering…'));
    try {
      const mod = await import('../showcase/remote.js');
      // `fresh` bypasses the worker's 5-minute Cache-Control. The whole point of this sheet is
      // "what is on the storefront RIGHT NOW", and the admin has usually just published.
      const defs = await mod.loadShowcaseSlides({ fresh: true });
      if (!defs.length) { gridEl.replaceChildren(h('p', { class: 'wd-dm-empty' }, 'Nothing published yet.')); return; }
      const tiles = [];
      for (const d of defs) {
        /* ⛔ REBUILD THE LOGO COMPOSITE FIRST — `logo.renderUrl` is a TRANSIENT that serialization
           drops, so a published example whose logo was recoloured/peeled/ringed renders from the
           ORIGINAL artwork instead. Same trap as the parade (showcase.js) and plate-batch (WD 178);
           a contact sheet showing pre-recolour logos would be worse than no sheet, because its whole
           job is answering "what colours have we used". */
        // eslint-disable-next-line no-await-in-loop
        await ctx.rebuildLogoComposite?.(d.profile);
        // No `uid` — each SVG mints its own. Passing a shared one would cross-wire the clipPaths and
        // gradients of every tile inlined into this one document.
        const svg = renderDesignToSVGString(d.profile, {
          weight: d.weight ?? d.profile?.weightValue ?? 45,
          product: d.profile?.product,
          size: 260,
        });
        tiles.push(h('figure', { class: 'wd-sc-tile' },
          h('div', { class: 'wd-sc-tile__art', html: svg }),
          h('figcaption', { class: 'wd-sc-tile__cap' }, d.name)));
      }
      gridEl.replaceChildren(...tiles);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[weight-designer] showcase preview failed:', err);
      gridEl.replaceChildren(h('p', { class: 'wd-dm-empty' }, 'Couldn’t render the previews.'));
    }
  }

  const previewBtn = h('button', {
    type: 'button', class: 'wd-sc-mini', title: 'Show every published slide as a flat contact sheet',
    onClick: async () => {
      if (!gridEl.hidden) { gridEl.hidden = true; previewBtn.textContent = 'Preview'; return; }
      gridEl.hidden = false;
      previewBtn.textContent = 'Hide preview';
      await paintPreview();
    },
  }, 'Preview');

  const body = h('div', { class: 'wd-sc' },
    h('p', { class: 'wd-sc-blurb' },
      'Publish your staged set as an example buyers see behind “Show me what’s possible”. '
      + 'One design per model — bumper, grip plate and dumbbell (which covers fixed barbells) — '
      + 'each shown at the layout and weight you designed it at.'),
    h('label', { class: 'wd-sc-field' }, h('span', { class: 'wd-dm-listhead__label' }, 'Name'), nameInput),
    h('label', { class: 'wd-sc-field' }, h('span', { class: 'wd-dm-listhead__label' }, 'Caption'), capInput),
    setLine,
    h('button', { type: 'button', class: 'wd-dm-save wd-sc-publish', onClick: () => void publish() }, 'Publish to showcase'),
    h('div', { class: 'wd-sc-sep' }),
    h('div', { class: 'wd-sc-listhead' },
      h('span', { class: 'wd-dm-listhead__label' }, 'On the storefront now'),
      previewBtn),
    gridEl,
    listEl,
    status,
  );

  paintSetLine();   // say what will be published BEFORE the button is reachable
  void refresh();
  return openModal({ mount: ctx.el, title: 'Show me what’s possible', body, actions: [{ id: 'done', label: 'Done', variant: 'ghost' }] });
}

/** The Save-card row that opens it. Empty for anyone who isn't an admin. */
export function showcaseItems(ctx) {
  if (!isShowcaseAdmin()) return [];
  return [{
    icon: ICON_SHOWCASE,
    label: 'Show me what’s possible…',
    sub: 'Publish this design as an example',
    onClick: () => { void openShowcaseDialog(ctx); },
  }];
}
